"use client";

import { Icon } from "@iconify/react";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "../../lib/utils";
import { SocialIcons } from "../ui/social-icons";
import { NavItem, NavGroup } from "./types";
import { useTranslation } from 'react-i18next';

interface MobileMenuProps {
  onClose: () => void;
  navItems: NavGroup[];
  theme: string | undefined;
  setTheme: (theme: string) => void;
  mounted: boolean;
}


export function MobileMenu({ onClose, navItems, theme, setTheme, mounted }: MobileMenuProps) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [isClosing, setIsClosing] = useState(false);

  const backdropRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  // Mount animation
  useEffect(() => {
    const tl = gsap.timeline();

    if (backdropRef.current) {
      tl.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
    }

    if (sidebarRef.current) {
      tl.fromTo(sidebarRef.current,
        { x: -250, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.3, ease: "power3.out" },
        "<"
      );
    }

    if (itemsContainerRef.current) {
      const children = itemsContainerRef.current.children;
      if (children.length > 0) {
        tl.fromTo(children,
          { x: -20, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.3, stagger: 0.05, ease: "power2.out" },
          "-=0.1"
        );
      }
    }
  }, []);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);

    const tl = gsap.timeline({ onComplete: onClose });

    if (itemsContainerRef.current) {
      const children = Array.from(itemsContainerRef.current.children).reverse();
      if (children.length > 0) {
        tl.to(children, { x: -20, opacity: 0, duration: 0.2, stagger: 0.03, ease: "power2.in" });
      }
    }

    if (sidebarRef.current) {
      tl.to(sidebarRef.current, { x: -250, opacity: 0, duration: 0.2, ease: "power3.in" }, "<");
    }

    if (backdropRef.current) {
      tl.to(backdropRef.current, { opacity: 0, duration: 0.2 }, "<");
    }
  };

  const renderNavItem = (item: NavItem) => {
    const isActive = mounted && (pathname === item.href ||
      (pathname && item.href !== "/" && pathname.startsWith(item.href)));

    return (
      <div
        key={item.href}
        className="w-full flex justify-center GSAP_NAV_ITEM"
      >
        <Link
          href={item.href}
          prefetch={false}
          onClick={handleClose}
          className="w-full px-4"
          title={t(`Navbar.${item.name}`)}
        >
          <div className={cn(
            "flex items-center h-10 px-4 rounded-lg w-full transition-all",
            isActive
              ? "bg-slate-100 dark:bg-slate-800/80 text-slate-900 dark:text-slate-100 font-medium shadow-sm"
              : "hover:bg-slate-100/80 dark:hover:bg-slate-800/50 text-slate-900 dark:text-slate-200",
            "group relative overflow-hidden"
          )}>
            <div
              className={cn(
                "mr-4 relative z-10 transition-transform duration-300",
                isActive ? "" : "group-hover:scale-110 group-hover:rotate-12"
              )}
            >
              <Icon
                icon={item.icon}
                width="20"
                height="20"
                className={cn(
                  isActive
                    ? "text-primary"
                    : "text-slate-600 dark:text-slate-300 group-hover:text-primary"
                )}
              />
            </div>
            <span className="relative z-10">{t(`Navbar.${item.name}`)}</span>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <>
      <div
        ref={backdropRef}
        className="fixed inset-0 bg-black/50 z-40"
        style={{ opacity: 0 }}
        onClick={handleClose}
      />

      <aside
        ref={sidebarRef}
        className="fixed top-0 left-0 h-full w-[280px] bg-white dark:bg-[#0a0a0a] flex flex-col py-6 z-50 shadow-xl"
        style={{ transform: "translateX(-250px)", opacity: 0 }}
        suppressHydrationWarning
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 mb-6">
          <div className="flex items-center">
            <Icon
              icon="solar:widget-2-bold-duotone"
              width="24"
              height="24"
              className="text-primary mr-2"
            />
            <span className="font-semibold text-lg">Menu</span>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-foreground rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 hover:scale-110 active:scale-95 transition-all"
            aria-label="Close sidebar"
          >
            <Icon
              icon="solar:close-square-linear"
              width="24"
              height="24"
              className="text-slate-500 dark:text-slate-400"
            />
          </button>
        </div>

        <nav className="flex-1 flex flex-col items-center space-y-6 pt-4">
          <div ref={itemsContainerRef} className="w-full flex flex-col items-center space-y-5">
            {navItems.flatMap(group => group.items.map(renderNavItem))}

            <button
              data-cal-namespace="30min"
              data-cal-link="ellis-caicedo-hfdo7f/30min"
              data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
              className="flex items-center justify-center gap-2 w-[calc(100%-2rem)] py-3 bg-[#181A1D] dark:bg-white text-white dark:text-[#181A1D] rounded-full text-sm font-bold shadow-lg shadow-black/10 dark:shadow-white/5 hover:scale-[1.02] active:scale-95 transition-all duration-300"
              onClick={handleClose}
            >
              <span>{t('Navbar.scheduleCall')}</span>
              <Icon icon="solar:arrow-right-up-linear" width="18" height="18" />
            </button>
          </div>
        </nav>

        {/* Theme Toggle */}
        <div className="flex justify-center mt-auto mb-6 GSAP_NAV_ITEM">
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 hover:scale-110 active:scale-95 transition-transform"
            aria-label="Toggle theme"
          >
            {mounted && (
              <Icon
                icon={theme === "dark" ? "solar:sun-2-bold-duotone" : "solar:moon-bold-duotone"}
                width="22"
                height="22"
                className={theme === "dark" ? "text-amber-400" : "text-slate-700"}
              />
            )}
          </button>
        </div>

        {/* Social Icons */}
        <div className="px-4 mt-4 GSAP_NAV_ITEM">
          <SocialIcons
            tooltipPosition="top"
            justifyContent="center"
            size="md"
            spacing="normal"
          />
        </div>
      </aside>
    </>
  );
}